import React from 'react';
import { UserPlus, Shield, Check, X, Clock } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';

const RequestsView = ({ friendRequests, teamRequests, onAcceptFriend, onRejectFriend, onAcceptTeam, onRejectTeam }) => {
    const { user } = useUser();

    return (
        <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-20 min-h-0">
            {/* Friend Requests */}
            <div>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <UserPlus size={14} /> Friend Requests
                </h3>
                {friendRequests.length === 0 ? (
                    <p className="text-gray-600 text-sm text-center py-4">No pending friend requests.</p>
                ) : (
                    <div className="space-y-2">
                        {friendRequests.map(req => (
                            <div key={req.id} className="bg-gray-800 p-3 rounded-xl border border-gray-700 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-bold">
                                    {req.fromName?.charAt(0) || '?'}
                                </div>
                                <div className="flex-1">
                                    <span className="text-white font-bold text-sm">{req.fromName || 'Unknown Player'}</span>
                                    <span className="block text-xs text-gray-500">wants to connect</span>
                                </div>
                                <button onClick={() => onAcceptFriend(req)} className="p-2 bg-green-600/20 text-green-400 rounded-full hover:bg-green-600 hover:text-white transition">
                                    <Check size={14} />
                                </button>
                                <button onClick={() => onRejectFriend(req)} className="p-2 bg-red-600/20 text-red-400 rounded-full hover:bg-red-600 hover:text-white transition">
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Team Requests */}
            <div>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Shield size={14} /> Team Requests
                </h3>
                {teamRequests.length === 0 ? (
                    <p className="text-gray-600 text-sm text-center py-4">No pending team requests.</p>
                ) : (
                    <div className="space-y-2">
                        {teamRequests.map(req => {
                            const isMyRequest = req.userId === user?.uid;
                            const isTeamInvite = req.type === 'invite';

                            // If it's a join_request from someone else (I am the owner)
                            const isIncomingJoinRequest = req.type === 'join_request' && !isMyRequest;

                            return (
                                <div key={req.id} className="bg-gray-800 p-3 rounded-xl border border-gray-700 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center font-bold">
                                        {/* If it's incoming request, show user initial. If invite/my request, show team initial */}
                                        {isIncomingJoinRequest ? (req.userName?.charAt(0) || 'U') : (req.teamName?.charAt(0) || 'T')}
                                    </div>
                                    <div className="flex-1">
                                        {isIncomingJoinRequest ? (
                                            <>
                                                <span className="text-white font-bold text-sm">{req.userName || 'Unknown Player'}</span>
                                                <span className="block text-xs text-gray-500">wants to join <b>{req.teamName}</b></span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-white font-bold text-sm">{req.teamName || 'Unknown Team'}</span>
                                                <span className="block text-xs text-gray-500">{isTeamInvite ? 'invited you' : 'pending approval'}</span>
                                            </>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    {(isTeamInvite || isIncomingJoinRequest) && (
                                        <>
                                            <button onClick={() => onAcceptTeam(req)} className="p-2 bg-green-600/20 text-green-400 rounded-full hover:bg-green-600 hover:text-white transition">
                                                <Check size={14} />
                                            </button>
                                            <button onClick={() => onRejectTeam(req)} className="p-2 bg-red-600/20 text-red-400 rounded-full hover:bg-red-600 hover:text-white transition">
                                                <X size={14} />
                                            </button>
                                        </>
                                    )}

                                    {/* Status only */}
                                    {!isTeamInvite && !isIncomingJoinRequest && (
                                        <span className="text-xs text-yellow-400 flex items-center gap-1"><Clock size={12} /> Pending</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RequestsView;
